import React, { useCallback, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Pagination,
  PaginationItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import PayoutInvoicesTable from './PayoutInvoicesTable';
import { usePayoutInvoices } from '../hooks/usePayoutInvoices';
import type { PayoutInvoiceListItem, PayoutInvoiceStatus } from '@/types/payout';
import { payoutsService } from '@/services';
import { useNotification } from '@/contexts/NotificationContext';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import { canPerformAction, ActionKey } from '@/auth/actionMatrix';

const UNDER_REVIEW_ONLY: PayoutInvoiceStatus[] = ['UNDER_REVIEW'];

function getHttpStatus(err: unknown): number | undefined {
  if (typeof err === 'object' && err !== null && 'response' in err) {
    const r = (err as { response?: { status?: number } }).response;
    return r?.status;
  }
  return undefined;
}

function verdictErrorMessage(status: number | undefined, fallback: string): string {
  if (status === 409) {
    return 'Заявка не в статусе «На проверке» или конфликт обработки';
  }
  if (status === 502) {
    return 'Сервис временно недоступен';
  }
  if (status === 503) {
    return 'Сервис недоступен';
  }
  return fallback;
}

/** Вкладка «Отменённые»: UNDER_REVIEW, вердикт по реквизитам через admin API. */
const PayoutsOutCancelled: React.FC = () => {
  const {
    invoices,
    error,
    loading,
    totalPages,
    filters,
    handlePageChange,
    handleRefresh,
    loadData,
  } = usePayoutInvoices({ fixedStatuses: UNDER_REVIEW_ONLY });
  const { showNotification } = useNotification();
  const user = useSelector((state: RootState) => state.auth.user);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [pendingCancel, setPendingCancel] = useState<PayoutInvoiceListItem | null>(null);

  const canVerdict = canPerformAction(user, ActionKey.PAYOUTS_OUT_INVOICE_REQUISITES_VERDICT);

  const submitVerdict = useCallback(
    async (row: PayoutInvoiceListItem, verdict: 'correct' | 'incorrect') => {
      if (!canVerdict) {
        showNotification('У вас нет прав для отправки вердикта', 'error');
        return;
      }
      try {
        setBusyId(row.id);
        const res = await payoutsService.postPayoutInvoiceRequisitesVerdict(row.id, { verdict });
        showNotification(res.detail || 'Вердикт отправлен', 'success');
        await loadData(filters);
      } catch (err: unknown) {
        const status = getHttpStatus(err);
        showNotification(verdictErrorMessage(status, 'Ошибка при отправке вердикта'), 'error');
      } finally {
        setBusyId(null);
        setPendingCancel(null);
      }
    },
    [canVerdict, filters, loadData, showNotification],
  );

  const requestCancelVerdict = useCallback(
    (row: PayoutInvoiceListItem) => {
      if (!canVerdict) {
        showNotification('У вас нет прав для отправки вердикта', 'error');
        return;
      }
      setPendingCancel(row);
    },
    [canVerdict, showNotification],
  );

  const closeCancelDialog = useCallback(() => {
    if (busyId) return;
    setPendingCancel(null);
  }, [busyId]);

  const confirmCancelVerdict = useCallback(() => {
    if (!pendingCancel) return;
    void submitVerdict(pendingCancel, 'incorrect');
  }, [pendingCancel, submitVerdict]);

  const renderRowActions = useCallback(
    (row: PayoutInvoiceListItem) => {
      const busy = busyId === row.id || loading;
      const notOwner = !row.is_claimed_by_me;
      return (
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Tooltip title={canVerdict ? '' : 'Недостаточно прав'}>
            <span>
              <Button
                size="small"
                variant="contained"
                color="primary"
                disabled={busy || !canVerdict || notOwner}
                onClick={() => requestCancelVerdict(row)}
              >
                Отмена
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={canVerdict ? '' : 'Недостаточно прав'}>
            <span>
              <Button
                size="small"
                variant="outlined"
                color="warning"
                disabled={busy || !canVerdict || notOwner}
                onClick={() => submitVerdict(row, 'correct')}
              >
                В пул
              </Button>
            </span>
          </Tooltip>
        </Stack>
      );
    },
    [busyId, canVerdict, loading, requestCancelVerdict, submitVerdict],
  );

  const cancelBusy = Boolean(pendingCancel && busyId === pendingCancel.id);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Отменённые
        </Typography>
        <Button variant="contained" onClick={() => handleRefresh()} disabled={loading} startIcon={<RefreshIcon />}>
          Обновить
        </Button>
      </Box>

      <PayoutInvoicesTable
        invoices={invoices}
        loading={loading}
        error={error}
        onClaimChanged={handleRefresh}
        allowClaim
        renderRowActions={renderRowActions}
      />

      {totalPages > 1 && (
        <Stack spacing={2} alignItems="center" sx={{ mt: 3 }}>
          <Pagination
            count={totalPages}
            page={filters.page}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
            renderItem={(item) => <PaginationItem {...item} disabled={item.page === filters.page} />}
          />
        </Stack>
      )}

      <Dialog open={Boolean(pendingCancel)} onClose={closeCancelDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Подтвердить перевод в отмену</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Вы точно уверены, что хотите отправить эту OUT-выплату в отмену?
          </DialogContentText>
          {pendingCancel && (
            <Stack spacing={0.5}>
              <Typography variant="body2">ID заявки: {pendingCancel.id}</Typography>
              <Typography variant="body2">Трейдер: {pendingCancel.trader_name || '-'}</Typography>
              <Typography variant="body2">Сумма: {pendingCancel.initial_amount}</Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCancelDialog} disabled={cancelBusy}>
            Назад
          </Button>
          <Button variant="contained" color="error" onClick={confirmCancelVerdict} disabled={cancelBusy}>
            {cancelBusy ? 'Отправка...' : 'Да, в отмену'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PayoutsOutCancelled;
